import mouseDownFocusHandler from '../helpers/mouseDownFocusHandler';
import debounce from '../helpers/debounce';
import lockBodyScroll from '../helpers/lockBodyScroll';
/**
 * MainNav
 */
export default class MainNav {
  /**
   * constructor
   * @param {object} options
   */
  constructor(options = {}) {
    this.config = {
      selector: {
        navList: '[data-main-nav]',
        subNavContainer: '[data-main-sub-nav]',
        navListItems: '[data-main-nav-item]',
        navListTrigger: '[data-main-nav-trigger]',
        headerTop: '[data-header-top-bar]',
        headerSub: '[data-header-sub-bar]',
        navListText: '[data-main-nav-text]',
        mobileNav: '[data-mobile-nav]',
        mobileNavTrigger: '[data-mobile-nav-trigger]',
        mobileNavBody: '[data-mobile-nav-body]',
        backBtn: '[data-main-nav-back-btn]',
      },
      mod: {
        subNavVisible: 'main-nav__subnav-container--visible',
        subNavMobileVisible: 'mobile-main-nav__subnav-container--visible',
        activeList: 'main-nav__list--visible',
        activeListMobile: 'mobile-main-nav__list--visible',
        activeItem: 'main-nav__item--visible',
        activeItemMobile: 'mobile-main-nav__item--visible',
        activeTextItem: 'main-nav__item-text--visible',
        activeTextItemMobile: 'mobile-main-nav__item-text--visible',
        headerSticky: 'header-top-bar--sticky',
        mobileNavOpend: 'mobile-nav__body--opend',
        burgerOpend: 'burger-menu--opend',
      },
      ...options,
    };
    this.mainNav = document.querySelector(this.config.selector.navList);
    this.mobileNav = document.querySelector(this.config.selector.mobileNav);
    if (!this.mainNav && !this.mobileNav) {
      return;
    }
    this.subNavContainer = document.querySelectorAll(this.config.selector.subNavContainer);
    this.navItems = document.querySelectorAll(this.config.selector.navListItems);
    this.navTextItems = document.querySelectorAll(this.config.selector.navListText);
    this.headerTop = document.querySelector(this.config.selector.headerTop);
    this.headerSub = document.querySelector(this.config.selector.headerSub);
    this.lastScrollPosition = 0;

    this.stickyHeader();
    this.bindEvents();
    this.responsiveHandler();
  }

  /**
   * handles switch between mobile- and desktop nav
   */
  responsiveHandler() {
    this.isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    this.unbindMobileEvents();
    this.unbindDesktopEvents();
    if (this.isDesktop) {
      lockBodyScroll(false);
      const burger = document.querySelector(this.config.selector.mobileNavTrigger);
      const mobileNavBody = document.querySelector(this.config.selector.mobileNavBody);
      burger.classList.remove(this.config.mod.burgerOpend);
      mobileNavBody.classList.remove(this.config.mod.mobileNavOpend);
      this.bindDesktopEvents();
    } else {
      this.bindMobileEvents();
    }
  }

  /**
   * bind events.
   * prepare event handler for custom binding/unbinding
   */
  bindEvents() {
    this.openMobileHandler = function (event) {
      this.openMobileNavBody(event);
    }.bind(this);
    this.dropDownHandler = function (event) {
      this.handleDropDown(event);
    }.bind(this);
    window.addEventListener('scroll', debounce(() => {
      this.stickyHeader();
    }, 10));
    window.addEventListener('resize', debounce(() => {
      this.responsiveHandler();
    }, 200));
  }

  /**
   * bind desktop events
   */
  bindDesktopEvents() {
    document.addEventListener('focus', this.dropDownHandler, { capture: true });
    document.addEventListener('mousedown', this.dropDownHandler, { capture: true });
  }

  /**
   * unbind desktop events
   */
  unbindDesktopEvents() {
    document.removeEventListener('focus', this.dropDownHandler, { capture: true });
    document.removeEventListener('mousedown', this.dropDownHandler, { capture: true });
  }

  /**
   * bind mobile events
   */
  bindMobileEvents() {
    document.addEventListener('click', this.openMobileHandler);
    document.addEventListener('mousedown', this.dropDownHandler, { capture: true });
  }

  /**
   * unbind mobile events
   */
  unbindMobileEvents() {
    document.removeEventListener('click', this.openMobileHandler);
    document.removeEventListener('mousedown', this.dropDownHandler, { capture: true });
  }

  /**
   * Opens mobile nav on click and prevents body from scrolling
   * @param {MouseEvent} event
   */
  openMobileNavBody(event) {
    const { target } = event;
    const burger = target.closest(this.config.selector.mobileNavTrigger);
    if (burger) {
      const isOpen = burger.classList.contains(this.config.mod.burgerOpend);
      const mobileNavBody = document.querySelector(this.config.selector.mobileNavBody);
      lockBodyScroll(!isOpen);
      burger.classList.toggle(this.config.mod.burgerOpend);
      mobileNavBody.classList.toggle(this.config.mod.mobileNavOpend);
    }
  }

  /**
   * Adds sticky css modifier when scrolling over the header height
   */
  stickyHeader() {
    const headerHeight = this.headerTop.offsetHeight + this.headerSub.offsetHeight;
    const scrollPosition = window.pageYOffset;
    if (scrollPosition > headerHeight && this.lastScrollPosition > scrollPosition) {
      const diff = scrollPosition - this.lastScrollPosition;
      if (diff < -30) {
        this.headerTop.classList.add(this.config.mod.headerSticky);
      }
    } else {
      this.headerTop.classList.remove(this.config.mod.headerSticky);
    }
    this.lastScrollPosition = scrollPosition <= 0 ? 0 : scrollPosition;
  }

  /**
   * handles dowopdown for first-level of navigation
   * and passes the top navigation items sub navigation dropdown to handle the visibility of items.
   * @param {(MouseEvent|FocusEvent)} event
   */
  handleDropDown(event) {
    const { target } = event;
    const navList = target.closest(this.config.selector.navList);
    const navItem = target.closest(this.config.selector.navListItems);
    const textItem = navItem?.querySelector(this.config.selector.navListText);
    if (navItem && textItem) {
      const isSubNav = target.closest(this.config.selector.subNavContainer);
      const submenu = navItem.querySelector(this.config.selector.subNavContainer);
      // eslint-disable-next-line max-len
      const listClass = this.isDesktop ? this.config.mod.activeList : this.config.mod.activeListMobile;
      if (!isSubNav) {
        // handle sub nav
        if (submenu) {
          navList.classList.add(listClass);
          this.handleElements(event, submenu, navItem, textItem);
        }
      } else if (!this.isDesktop) {
        const isBackBtn = target.closest(this.config.selector.backBtn);
        navList.classList.remove(listClass);
        if (isBackBtn) {
          // handle back btn
          this.handleElements(event, null, null, null);
        } else {
          this.handleElements(event, submenu, navItem, textItem);
        }
      }
    } else if (this.isDesktop) {
      this.handleElements(event, null, null, null);
    }
  }

  /**
   * handles visibility of nav-items on mousedown and focus
   * @param {(MouseEvent|FocusEvent)} event
   * @param {Element} submenu
   * @param {HTMLElement} navItem
   * @param {Element} textItem
   */
  handleElements(event, submenu, navItem, textItem) {
    // eslint-disable-next-line max-len
    const containerModifier = this.isDesktop ? this.config.mod.subNavVisible : this.config.mod.subNavMobileVisible;
    // eslint-disable-next-line max-len
    const navItemModifier = this.isDesktop ? this.config.mod.activeItem : this.config.mod.activeItemMobile;
    // eslint-disable-next-line max-len
    const navTextItemModifier = this.isDesktop ? this.config.mod.activeTextItem : this.config.mod.activeTextItemMobile;

    this.subNavContainer.forEach((item) => {
      mouseDownFocusHandler(event.type, item, submenu, containerModifier, this.isDesktop);
    });
    this.navItems.forEach((item) => {
      mouseDownFocusHandler(event.type, item, navItem, navItemModifier, this.isDesktop);
    });
    this.navTextItems.forEach((item) => {
      mouseDownFocusHandler(event.type, item, textItem, navTextItemModifier, this.isDesktop);
    });
  }
}
